import { ReactNode, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { AddAccount } from '@/features/account-actions';

import {
  CurrencyAccountItem,
  ICurrencyAccount,
  useAccountsHook,
  useTotalBalanceStore,
  useVabsAccountsStore,
} from '@/entities/currency-account';

import {
  Button,
  DashboardDialog,
  Header,
  SmallCloseButton,
  Typography,
} from '@/shared/ui';

import { CurrencyAccountSelectTrigger } from './currency-select-trigger';

//TODO: fsd, combine all account selects into one

const Content = ({
  onSelect,
  selected,
  excludeAllAccounts,
  excludedCodes,
  onAddAccount,
  isCurrencyOnly,
  items,
  isWide,
  hasRequested,
  onClose,
}: Pick<
  CurrencyAccountSelectProps,
  | 'onSelect'
  | 'selected'
  | 'excludeAllAccounts'
  | 'isCurrencyOnly'
  | 'excludedCodes'
  | 'onAddAccount'
  | 'items'
  | 'isWide'
  | 'onClose'
  | 'hasRequested'
>) => {
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const requested = items ? items.filter((i) => i.status !== 'ACTIVE') : [];
  const totalAcc = useVabsAccountsStore((state) => state.totalBalanceAcc);
  const totalBalance = useTotalBalanceStore((state) => state.totalBalance);
  const filteredItems = items
    ? items.filter((item) => {
      return (
        item.status === 'ACTIVE' &&
        (excludeAllAccounts ? item.currencyName !== 'All accounts' : true) &&
        !excludedCodes?.includes(item.code)
      );
    })
    : [];
  return (
    <div className="flex h-full flex-1 flex-col">
      <div className="flex-1 px-4">
        {hasRequested &&
          !!requested.length &&
          requested.map((item, idx) => (
            <CurrencyAccountItem
              onClick={onSelect}
              item={item}
              className={twMerge(isWide && 'h-[72px]')}
              disabled
              key={item.id}
            />
          ))}
        {filteredItems.map((item, idx) => (
          <CurrencyAccountItem
            item={item}
            className={twMerge(isWide && 'h-[72px]')}
            disabled={item.status !== 'ACTIVE'}
            onClick={onSelect}
            isSelected={selected?.id === item.id}
            description={
              !excludeAllAccounts
                ? idx === filteredItems.length - 1
                  ? `${String(filteredItems.length - 1)} Accounts`
                  : ''
                : undefined
            }
            key={item.id}
          />
        ))}
        {hasRequested && totalAcc && totalBalance && (
          <CurrencyAccountItem
            item={{ ...totalAcc, value: +totalBalance?.amount }}
            className={twMerge(isWide && 'h-[72px]')}
            onClick={onSelect}
            isSelected={selected?.id === 'all'}
            description={`${String(filteredItems.length)} accounts`}
            key="all"
            isAll
          />
        )}
      </div>

      {!isCurrencyOnly && (
        <div className="mt-4 flex w-full items-center justify-center gap-1 pb-6">
          <Typography variant="13-r" color="text-base-100">
            Currency not listed?
          </Typography>
          <Button
            variant="clean"
            onClick={() => {
              setIsAddAccountOpen(true);
            }}
          >
            <Typography variant="13-r" color="brand-500">
              Open an account
            </Typography>
          </Button>
        </div>
      )}
      <AddAccount
        open={isAddAccountOpen}
        onOpenChange={setIsAddAccountOpen}
        onConfirmAdding={() => {
          setIsAddAccountOpen(false);
          onClose?.();
        }}
      >
        <></>
      </AddAccount>
    </div>
  );
};

interface CurrencyAccountSelectProps {
  onSelect: (item: ICurrencyAccount) => void;
  selected: ICurrencyAccount;
  excludeAllAccounts?: boolean;
  title?: 'Accounts' | 'Choose a currency';
  customTrigger?: ReactNode;
  isCurrencyOnly?: boolean;
  excludedCodes?: string[];
  onAddAccount?: VoidFunction;
  items?: ICurrencyAccount[];
  isWide?: boolean;
  onClose?: () => void;
  hasRequested?: boolean;
  isDisabled?: boolean;
}

export const CurrencyAccountSelect = ({
  onSelect,
  selected,
  title = 'Accounts',
  isCurrencyOnly,
  customTrigger,
  excludeAllAccounts = true,
  onAddAccount,
  excludedCodes,
  isWide,
  hasRequested,
  isDisabled,
}: CurrencyAccountSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading } = useAccountsHook();

  return (
    <DashboardDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      contentClassName="gap-0 max-h-[600px]"
      header={
        <Header
          className="p-5"
          title={title}
          action={{
            as: 'custom',
            render: <SmallCloseButton />,
          }}
        />
      }
      content={
        !isLoading && (
          <Content
            hasRequested={hasRequested}
            items={data}
            onSelect={(item) => {
              onSelect(item);
              setIsOpen(false);
            }}
            onAddAccount={() => {
              setIsOpen(false);
              onAddAccount?.();
            }}
            onClose={() => {
              setIsOpen(false);
            }}
            excludedCodes={excludedCodes}
            isCurrencyOnly={isCurrencyOnly}
            selected={selected}
            excludeAllAccounts={excludeAllAccounts}
            isWide={isWide}
          />
        )
      }
      hideCloseButton
      animationProps={{ isFirst: true }}
    >
      {customTrigger ?? (
        <CurrencyAccountSelectTrigger
          isOpen={isOpen}
          foundItem={selected}
          placeholder="Currency"
        />
      )}
    </DashboardDialog>
  );
};
